import { Ref, useCallback, useImperativeHandle } from "react";
import { useTranslation } from "@coworker/locales";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import {
  isLoading,
  Loading,
} from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import {
  getByPath,
  setByPath,
  isRecord,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import SelectListItem, {
  ListItem,
} from "@coworker/apprestructured/src/task-management/components/SelectListItem/SelectListItem";
import {
  shouldShowField,
  VisibilityCondition,
} from "@coworker/apprestructured/src/task-management/helpers/visibilityCondition.helper";
import { shouldStoreValueOnly } from "@coworker/apprestructured/src/shared/helpers/task.helper";
import { useResetFields } from "@coworker/apprestructured/src/task-management/services/useResetFields";

export type SelectListItemCreatorFragmentConfigProps = {
  title: string;
  field: string;
  items: ListItem[];
  visibility?: VisibilityCondition;
  resetFieldsOnChange?: string[];
};

type SelectListItemCreatorFragmentProps = {
  configProps: SelectListItemCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function isListItem(value: unknown): value is ListItem {
  return (
    isRecord(value) &&
    typeof value["value"] === "string" &&
    typeof value["label"] === "string"
  );
}

function getListItemFromRawValue(
  rawValue: unknown | Loading
): ListItem | null | Loading {
  if (isLoading(rawValue)) {
    return rawValue;
  }
  if (isListItem(rawValue)) {
    return rawValue;
  }
  return null;
}

function SelectListItemCreatorFragment({
  configProps,
  commonProps,
  ref,
}: SelectListItemCreatorFragmentProps) {
  const { field, title, items, visibility, resetFieldsOnChange } = configProps;
  const { task, onTaskChange, taskType, showValidation } = commonProps;
  const { t } = useTranslation();

  const resetFields = useResetFields(taskType);

  // Determine storage format from schema
  const storeValueOnly = shouldStoreValueOnly(taskType, field);

  const rawValue = getByPath(task, field);
  const listItem = getListItemFromRawValue(rawValue);

  const getItemByValue = useCallback(
    (value: string) => items.find((item) => item.value === value) ?? null,
    [items]
  );

  const selectedItem = (() => {
    // Handle string value (when storeValueOnly is used)
    if (storeValueOnly && typeof rawValue === "string") {
      return getItemByValue(rawValue);
    }
    // Handle object value
    if (isLoading(listItem) || !listItem?.value) {
      return null;
    }
    return getItemByValue(listItem.value);
  })();

  const handleConfirmClick = useCallback(
    (newItem: ListItem | null) => {
      const valueToStore = storeValueOnly
        ? (newItem?.value ?? "")
        : { value: newItem?.value ?? "", label: newItem?.label ?? "" };
      onTaskChange(
        setByPath(field, valueToStore, resetFields(resetFieldsOnChange ?? []))
      );
    },
    [field, onTaskChange, storeValueOnly, resetFields, resetFieldsOnChange]
  );

  const valid = (() => {
    if (storeValueOnly) {
      return typeof rawValue === "string" && rawValue !== "";
    }
    return !isLoading(listItem) && listItem !== null;
  })();

  useImperativeHandle(ref, () => {
    return { valid };
  });

  if (visibility && !isLoading(task) && !shouldShowField(task, visibility)) {
    return null;
  }

  if (isLoading(rawValue)) {
    return <LoadingCreatorFragment />;
  }

  return (
    <SelectListItem
      title={title}
      selectedItem={selectedItem}
      items={items}
      onConfirmClick={handleConfirmClick}
      showError={showValidation && !valid}
      errorMessage={t("pleaseSelectString")}
    />
  );
}

export default SelectListItemCreatorFragment;
